class MedispaBookingToast extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  uuidv4() {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
      (
        +c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
      ).toString(16),
    );
  }

  /**
   * @typedef Toast
   *
   * @property guid {string}
   * @property productImage {string}
   * @property customerName {string}
   * @property productName {string}
   * @property timestamp {string}
   * @property rendered {boolean}
   */

  /**
   * @type {Toast[]}
   */
  toasts = [];

  // 3 minutes
  lifespan_ms = 180000;
  timestamp_update_interval_ms = 5000;
  zoom_coefficient = 0.95;
  translate_amount = -10;
  visible_count = 3;

  customCSS = ``;

  relativeTimer = new Intl.RelativeTimeFormat(window.Shopify.locale, {
    style: "long",
  });

  connectedCallback() {
    const start = () => {
      this.lifespan_ms =
        window.bookingToastsConfig.booking_toasts_lifespan_seconds * 1000;
      this.timestamp_update_interval_ms =
        window.bookingToastsConfig.booking_toasts_timestamp_interval_seconds *
        1000;

      this.customCSS = window.bookingToastsConfig.booking_toasts_custom_css;

      this.visible_count =
        window.bookingToastsConfig.booking_toasts_visible_count;

      this.render();
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", start);
    } else {
      start();
    }
  }

  getStyles() {
    return `
    .toasts-container {
      position: fixed;
      bottom: 0;
      left: 0;
      max-width: 720px;
      width: calc(100% - 84px);
      height: 150px;
      margin: 0 auto;
      display: block;
      pointer-events: none;
      z-index: 11;
    }
    
    .toast {
      position: absolute;
      background: white;
      border-radius: 200px;
      bottom: 1rem;
      left: 1rem;
      overflow: hidden;
      display: flex;
      gap: 12px;
      padding: 0.5rem 1rem;
      box-shadow: 0px 5px 10px 0px rgb(0 0 0 / 25%);
      animation-name: slide-in;
      animation-duration: 300ms;
      animation-iteration-count: 1;
      animation-fill-mode: forwards;
      transform-origin: top;
      transition-property: transform opacity;
      transition-duration: 0.3s;
      transition-timing-function: ease;
      min-width: 300px;
    }
    
    .product-image__wrapper {
      width: 72px;
      height: 72px;
      aspect-ratio: 1;
      object-fit: cover;
      border-radius: 50%;
      overflow: hidden;
      flex-shrink: 0;
    }
    
    .product-image {
      width: 100%;
      height: 100%;
      aspect-ratio: 1;
      object-fit: cover;
    }
    
    .product-details {
      display: flex;
      flex-direction: column;
      gap: 8px;
      justify-content: center;
      align-items: flex-start;
    }

    .product-details > span {
      display: flex;
      flex-direction: column;
    }
    
    .fade-out {
      animation-name: fade-out;
      animation-duration: 1000ms;
      animation-iteration-count: 1;
      animation-fill-mode: forwards;
    }
    
    @keyframes slide-in {
      from {
        translate: 0 200px;
      }
      to {
        translate: 0 0;
      }
    }
    
    @keyframes fade-out {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }

    @media only screen and (max-width: 767px) {
      .toasts-container {
        pointer-events: none;
        left: 0;
      }

      .toast {
        bottom: 3rem;
        left: 0;
        min-width: unset;
      }
    }

    ${this.customCSS}`;
  }

  /**
   *
   * @param {string} customerName
   * @param {string} productName
   * @returns {Toast}
   */
  createToast(customerName, productName) {
    return {
      guid: this.uuidv4(),
      productImage: window.productData[0].featured_image,
      customerName,
      productName,
      timestamp: new Date().valueOf(),
      rendered: false,
    };
  }

  getMarkup() {
    return this.toasts.reduce((all, toast) => {
      if (toast.rendered) return all;

      all += window.bookingToastsConfig.booking_toasts_copy
        .replace(/{guid}/g, toast.guid)
        .replace(/{product_image}/g, toast.productImage)
        .replace(/{customer_name}/g, toast.customerName)
        .replace(/{product_name}/g, toast.productName)
        .replace(/{timestamp}/g, toast.timestamp);

      toast.rendered = true;

      return all;
    }, "");
  }

  updateTimestamps() {
    let now = new Date().valueOf();

    // Update timestamps on all toasts
    this.shadowRoot.querySelectorAll(".timestamp").forEach((el) => {
      let timestamp = parseInt(el.getAttribute("data-timestamp"));
      let diffMs = timestamp - now;
      let diffSec = Math.ceil(diffMs / 1000);

      let relativeTime = window.bookingToastsConfig.booking_toasts_just_now_label;

      let absDiff = Math.abs(diffSec);

      if (absDiff > 0) {
        if (absDiff >= 60)
          relativeTime = this.relativeTimer.format(
            Math.ceil(diffSec / 60),
            "minute",
          );
        else relativeTime = this.relativeTimer.format(diffSec, "second");
      }

      el.innerText = relativeTime;
    });
  }

  renderToasts() {
    let newToastMarkup = this.getMarkup();
    this.shadowRoot
      .querySelector(".toasts-container")
      .insertAdjacentHTML("beforeend", newToastMarkup);

    // Update transform on all toasts if new ones were added to the DOM
    if (newToastMarkup) {
      let toasts = this.shadowRoot.querySelectorAll(".toast");
      let toastCount = toasts.length;

      toasts.forEach((el, i) => {
        let translation = parseFloat(el.dataset.translation);

        if (isNaN(translation)) {
          translation = 0;
        }

        let scale = parseFloat(el.dataset.scale);

        if (isNaN(scale)) {
          scale = 1.052631578947368;
        }

        let inverseIndex = toastCount - i - 1;

        let opacity = inverseIndex >= this.visible_count ? 0 : 1;

        let newTranslation =
          translation + this.translate_amount * (1 / this.zoom_coefficient);

        let newScale = scale * this.zoom_coefficient;

        let transform = `translate(0, ${newTranslation}px) scale(${newScale})`;
        el.style.transform = transform;

        el.dataset.translation = newTranslation;
        el.dataset.scale = newScale;
        el.style.opacity = opacity;
      });
    }

    this.updateTimestamps();
  }

  cullOldToasts() {
    // Cull any toasts that have exceeded the lifespan
    return this.toasts.filter((toast, idx, list) => {
      let diffMs = toast.timestamp - new Date().valueOf();
      let timeAlive = Math.abs(diffMs);

      if (toast.rendered && 
        timeAlive > this.lifespan_ms) {
        let toastElem = this.shadowRoot.querySelector(
          `.toast[data-guid='${toast.guid}']`,
        );

        let inverseIndex = list.length - idx - 1;

        if (inverseIndex < this.visible_count) {
          toastElem?.classList.toggle("fade-out");
        }

        setTimeout(() => {
          toastElem?.remove();
        }, 1000);
      }
      return timeAlive < this.lifespan_ms;
    });
  }

  toggleSmileLauncher() {
    document
      .querySelector(".smile-launcher-frame-container")
      ?.classList.toggle("shift-up", !!this.toasts.length);
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>${this.getStyles()}</style>

      <div class="toasts-container"></div>
    `;

    document.body.addEventListener(
      "booking-availability.appointment-booked",
      (e) => {
        const data = e.detail;

        this.toasts.push(this.createToast(data.customerName, data.productName));

        this.toggleSmileLauncher();

        this.renderToasts();
      },
    );

    setTimeout(() => {
      const runner = () => {
        this.toasts = this.cullOldToasts();

        this.toggleSmileLauncher();

        if (this.toasts.length > 0) {
          this.updateTimestamps();
        }

        setTimeout(runner, this.timestamp_update_interval_ms);
      };

      runner();
    }, this.timestamp_update_interval_ms);
  }
}

customElements.define("medispa-booking-toasts", MedispaBookingToast);
